import {
  createPublicClient,
  createWalletClient,
  custom,
  decodeEventLog,
  formatUnits,
  getAddress,
  http,
  isAddress,
  zeroAddress,
  type Address,
  type EIP1193Provider,
  type Hash,
  type TransactionReceipt, fallback, parseAbiItem } from 'viem';
import { bsc, bscTestnet } from 'viem/chains';
import { explorerUrlFor } from './network';
import { isMemberId } from './format';

const coreAbi = [
  {
    type: 'function',
    name: 'walletOf',
    stateMutability: 'view',
    inputs: [{ name: 'bDynamicId', type: 'uint256' }],
    outputs: [{ name: '', type: 'address' }],
  },
  {
    type: 'function',
    name: 'idsByWallet',
    stateMutability: 'view',
    inputs: [{ name: 'wallet', type: 'address' }],
    outputs: [{ name: '', type: 'uint256[]' }],
  },
  {
    type: 'function',
    name: 'maxIdsPerWallet',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint32' }],
  },
  {
    type: 'function',
    name: 'effectiveEra',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint8' }],
  },
  {
    type: 'function',
    name: 'currentEntryAmount',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'joiningFrozen',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      { name: 'frozen', type: 'bool' },
      { name: 'until', type: 'uint64' },
    ],
  },
  {
    type: 'function',
    name: 'join',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'sponsorId', type: 'uint256' },
      { name: 'expectedEra_', type: 'uint8' },
      { name: 'declaredAmount', type: 'uint256' },
      { name: 'deadline', type: 'uint64' },
    ],
    outputs: [{ name: 'bDynamicId', type: 'uint256' }],
  },
  {
    type: 'event',
    name: 'IdCreated',
    anonymous: false,
    inputs: [
      { indexed: true, name: 'bDynamicId', type: 'uint256' },
      { indexed: true, name: 'wallet', type: 'address' },
      { indexed: true, name: 'sponsorId', type: 'uint256' },
      { indexed: false, name: 'era', type: 'uint8' },
      { indexed: false, name: 'entryAmount', type: 'uint256' },
      { indexed: false, name: 'startingSlotCode', type: 'uint16' },
      { indexed: false, name: 'matrixVersion', type: 'uint64' },
    ],
  },
  { type: 'error', name: 'InvalidSponsor', inputs: [{ name: 'sponsorId', type: 'uint256' }] },
  {
    type: 'error',
    name: 'WalletIdLimit',
    inputs: [
      { name: 'wallet', type: 'address' },
      { name: 'limit', type: 'uint256' },
    ],
  },
  {
    type: 'error',
    name: 'IncorrectEntryAmount',
    inputs: [
      { name: 'expected', type: 'uint256' },
      { name: 'declared', type: 'uint256' },
    ],
  },
  {
    type: 'error',
    name: 'ExpectedEraChanged',
    inputs: [
      { name: 'expected', type: 'uint8' },
      { name: 'actual', type: 'uint8' },
    ],
  },
  { type: 'error', name: 'DeadlineExpired', inputs: [{ name: 'deadline', type: 'uint64' }, { name: 'currentTime', type: 'uint64' }] },
  { type: 'error', name: 'JoiningFrozen', inputs: [{ name: 'until', type: 'uint64' }] },
] as const;

const paymentTokenAbi = [
  {
    type: 'function',
    name: 'decimals',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint8' }],
  },
  {
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'allowance',
    stateMutability: 'view',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'approve',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
] as const;

export type JoinEra = 0 | 1 | 2;

export interface ProtocolConfiguration {
  chainId: 56 | 97;
  coreAddress: Address;
  paymentTokenAddress: Address;
  rpcUrl: string;
  /** Extra public endpoints tried in order when rpcUrl fails; the API has failover, the browser did not. */
  rpcFallbackUrls: string[];
  explorerUrl: string;
  confirmations: number;
  deadlineSeconds: number;
}

export type ProtocolAvailability =
  | { configured: true; config: ProtocolConfiguration }
  | { configured: false; reason: string };

export interface JoinQuote {
  sponsorId: bigint;
  sponsorWallet: Address;
  era: JoinEra;
  entryAmount: bigint;
  tokenDecimals: number;
  tokenBalance: bigint;
  nativeBalance: bigint;
  confirmedBlock: bigint;
  currentIdCount: number;
  /** IDs this wallet already holds, so the UI can warn before a second one is created. */
  existingIds: readonly bigint[];
  maxIdsPerWallet: number;
}

export type JoinProgressStage =
  | 'approval-submitted'
  | 'approval-confirming'
  | 'approval-replaced'
  | 'approval-finalized'
  | 'approval-skipped'
  | 'join-submitted'
  | 'join-confirming'
  | 'join-replaced'
  | 'join-finalized';

export interface JoinProgress {
  stage: JoinProgressStage;
  hash?: Hash;
  /** Block just before the join was sent: lets Retry find the join by its IdCreated event if the wallet's hash was unusable. */
  fromBlock?: bigint;
  replacementReason?: 'cancelled' | 'repriced' | 'replaced';
}

export interface JoinResult {
  memberId: string;
  approvalHash?: Hash;
  joinHash: Hash;
  blockNumber: bigint;
}

export class JoinFlowError extends Error {
  readonly code: string;
  readonly retryable: boolean;
  /** The underlying wallet/RPC text, kept so the UI can show it instead of only a friendly summary. */
  readonly detail: string | undefined;

  constructor(code: string, message: string, retryable = true, detail?: string) {
    super(message);
    this.name = 'JoinFlowError';
    this.code = code;
    this.retryable = retryable;
    this.detail = detail;
  }
}

function parseBoundedInteger(value: string | undefined, fallback: number, minimum: number, maximum: number): number {
  if (!value) return fallback;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= minimum && parsed <= maximum ? parsed : fallback;
}

function configuredAddress(value: string | undefined): Address | null {
  if (!value || !isAddress(value)) return null;
  const address = getAddress(value);
  return address === zeroAddress ? null : address;
}

export function resolveProtocolConfiguration(env: ImportMetaEnv = import.meta.env): ProtocolAvailability {
  const chainId = Number(env.VITE_CHAIN_ID ?? 97);
  if (chainId !== 56 && chainId !== 97) {
    return { configured: false, reason: 'The configured chain must be BNB Smart Chain Testnet (97) or Mainnet (56).' };
  }

  if (chainId === 56 && env.VITE_ALLOW_MAINNET_TRANSACTIONS !== 'true') {
    return { configured: false, reason: 'Mainnet transactions are locked until the audited release explicitly enables them.' };
  }

  const coreAddress = configuredAddress(env.VITE_PROTOCOL_PROXY_ADDRESS);
  const paymentTokenAddress = configuredAddress(env.VITE_PAYMENT_TOKEN_ADDRESS);
  if (!coreAddress || !paymentTokenAddress) {
    return { configured: false, reason: 'Verified protocol proxy and BTCB token addresses have not been configured.' };
  }

  const defaultRpc = chainId === 56
    ? 'https://bsc-dataseed.bnbchain.org'
    : 'https://data-seed-prebsc-1-s1.bnbchain.org:8545';

  return {
    configured: true,
    config: {
      chainId,
      coreAddress,
      paymentTokenAddress,
      rpcUrl: env.VITE_RPC_PUBLIC_URL?.trim() || defaultRpc,
      rpcFallbackUrls: parseRpcList(env.VITE_RPC_FALLBACK_URLS, chainId),
      explorerUrl: explorerUrlFor(chainId),
      confirmations: parseBoundedInteger(env.VITE_FINALITY_CONFIRMATIONS, 5, 1, 1_000),
      deadlineSeconds: parseBoundedInteger(env.VITE_TRANSACTION_DEADLINE_SECONDS, 600, 60, 3_600),
    },
  };
}

export function memberIdToUint(memberId: string): bigint {
  if (!isMemberId(memberId)) throw new JoinFlowError('invalid-sponsor-format', 'Enter a valid B-Dynamic sponsor ID.');
  return BigInt(memberId.slice(3));
}

export function uintToMemberId(memberId: bigint): string {
  if (memberId < 1n || memberId > 999_999_999n) {
    throw new JoinFlowError('invalid-created-id', 'The confirmed transaction returned an invalid B-Dynamic ID.', false);
  }
  // Sequential, unpadded: the genesis member is BD-1, the next join BD-2, and so on.
  return `BD-${memberId.toString()}`;
}

export function confirmedBlockNumber(latestBlock: bigint, confirmations: number): bigint {
  const lag = BigInt(Math.max(0, confirmations - 1));
  return latestBlock > lag ? latestBlock - lag : 0n;
}

export function needsExactApproval(currentAllowance: bigint, entryAmount: bigint): boolean {
  return currentAllowance !== entryAmount;
}

export function formatJoinAmount(amount: bigint, decimals: number): string {
  return formatUnits(amount, decimals).replace(/\.0+$/, '');
}

function eraFromChain(value: number): JoinEra {
  if (value === 0 || value === 1 || value === 2) return value;
  throw new JoinFlowError('invalid-era', 'The protocol returned an unsupported entry era.', false);
}

/**
 * Comma-separated extra RPC endpoints, with sensible public defaults per chain. The primary
 * bnbchain data-seed node has already been seen rate-limiting and dropping methods; a wallet flow
 * that depends on it alone dies with a generic error the moment it hiccups mid-transaction.
 */
function parseRpcList(value: string | undefined, chainId: number): string[] {
  const configured = (value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter((item) => /^https:\/\//.test(item));
  if (configured.length > 0) return configured;
  return chainId === 56
    ? ['https://bsc-dataseed1.bnbchain.org', 'https://bsc-rpc.publicnode.com']
    : ['https://bsc-testnet-rpc.publicnode.com', 'https://97.rpc.thirdweb.com'];
}

function makePublicClient(config: ProtocolConfiguration) {
  const endpoints = [config.rpcUrl, ...config.rpcFallbackUrls.filter((url) => url !== config.rpcUrl)];
  return createPublicClient({
    chain: config.chainId === 56 ? bsc : bscTestnet,
    // Ordered failover: each endpoint gets two retries before the next is tried, and a request
    // only fails once every endpoint has failed. `rank: false` keeps the configured order so the
    // primary is always preferred when healthy.
    transport: fallback(
      endpoints.map((url) => http(url, { timeout: 10_000, retryCount: 2, retryDelay: 400 })),
      { rank: false },
    ),
  });
}

function errorDetails(cause: unknown): { text: string; providerCode?: number } {
  const parts: string[] = [];
  let providerCode: number | undefined;
  let current: unknown = cause;

  for (let depth = 0; depth < 12 && current && typeof current === 'object'; depth += 1) {
    const item = current as { name?: unknown; message?: unknown; shortMessage?: unknown; details?: unknown; code?: unknown; cause?: unknown };
    for (const value of [item.name, item.shortMessage, item.details, item.message]) {
      if (typeof value === 'string') parts.push(value);
    }
    if (providerCode === undefined && typeof item.code === 'number') providerCode = item.code;
    current = item.cause;
  }

  if (typeof cause === 'string') parts.push(cause);
  return { text: parts.join(' | '), ...(providerCode === undefined ? {} : { providerCode }) };
}

export function normalizeJoinError(cause: unknown): JoinFlowError {
  if (cause instanceof JoinFlowError) return cause;
  const { text, providerCode } = errorDetails(cause);
  const normalized = text.toLowerCase();
  const detail = text.length > 320 ? `${text.slice(0, 320)}…` : text || undefined;

  if (providerCode === 4001 || /user rejected|user denied|request rejected/.test(normalized)) {
    return new JoinFlowError('wallet-rejected', 'The wallet request was rejected. No further transaction was submitted.', true, detail);
  }
  if (/insufficient funds|exceeds balance|insufficient balance/.test(normalized)) {
    return new JoinFlowError('insufficient-gas', 'The wallet does not have enough BNB to pay network gas.', true, detail);
  }
  if (/invalidsponsor|invalid sponsor/.test(normalized)) {
    return new JoinFlowError('invalid-sponsor', 'That sponsor is not present in finalized contract state. Return to Sponsor and check the ID.', true, detail);
  }
  if (/walletidlimit|wallet id limit/.test(normalized)) {
    return new JoinFlowError('wallet-limit', 'This wallet has reached the protocol limit for B-Dynamic IDs.', false, detail);
  }
  if (/expectederachanged|incorrectentryamount|expected era changed|incorrect entry amount/.test(normalized)) {
    return new JoinFlowError('quote-changed', 'The live era or entry amount changed. Review a fresh quote before trying again.', true, detail);
  }
  if (/joiningfrozen|joining frozen/.test(normalized)) {
    return new JoinFlowError('joining-frozen', 'Joining is temporarily frozen while the protocol finalizes an era transition.', true, detail);
  }
  if (/deadlineexpired|deadline expired/.test(normalized)) {
    return new JoinFlowError('deadline-expired', 'The transaction deadline expired before mining. Request a fresh transaction.', true, detail);
  }
  if (/replacement.*cancel|transaction.*cancel/.test(normalized)) {
    return new JoinFlowError('transaction-cancelled', 'The transaction was cancelled or replaced in the wallet. You can safely retry.', true, detail);
  }
  if (/timed out|timeout/.test(normalized)) {
    return new JoinFlowError('confirmation-timeout', 'Confirmation timed out. Check the transaction in BscScan before retrying.', true, detail);
  }
  if (/revert|execution error|call exception/.test(normalized)) {
    return new JoinFlowError('contract-reverted', 'The contract rejected the transaction. Refresh the quote and try again.', true, detail);
  }
  // Transport-level failures: the request never got a proper answer. Any transaction already
  // sent may still be mining, which is exactly why Retry resumes from the last hash instead of
  // resubmitting.
  if (
    /httprequesterror|rpcrequesterror|internalrpcerror|limitexceededrpcerror|resourceunavailablerpcerror|http request failed|fetch failed|failed to fetch|network ?error|load failed|internal json-rpc error|rate limit|too many requests|econnreset|socket hang up|\b(429|502|503|504)\b/.test(
      normalized,
    ) ||
    providerCode === -32603 ||
    providerCode === -32005
  ) {
    return new JoinFlowError(
      'rpc-unavailable',
      'The network RPC did not answer. Nothing is lost - if a transaction was already sent, Retry will check it before submitting anything new.',
      true,
      detail,
    );
  }
  return new JoinFlowError('wallet-or-rpc-error', 'The wallet or RPC provider could not complete the request. Check the network and try again.', true, detail);
}

export async function loadJoinQuote(
  config: ProtocolConfiguration,
  account: Address,
  sponsorMemberId: string,
): Promise<JoinQuote> {
  const publicClient = makePublicClient(config);
  const sponsorId = memberIdToUint(sponsorMemberId);

  try {
    const latestBlock = await publicClient.getBlockNumber();
    const confirmedBlock = confirmedBlockNumber(latestBlock, config.confirmations);
    const [sponsorWallet, eraValue, entryAmount, freeze, tokenDecimals, currentIds, maxIdsPerWallet] = await Promise.all([
      publicClient.readContract({
        address: config.coreAddress,
        abi: coreAbi,
        functionName: 'walletOf',
        args: [sponsorId],
        blockNumber: confirmedBlock,
      }),
      publicClient.readContract({
        address: config.coreAddress,
        abi: coreAbi,
        functionName: 'effectiveEra',
        blockNumber: confirmedBlock,
      }),
      publicClient.readContract({
        address: config.coreAddress,
        abi: coreAbi,
        functionName: 'currentEntryAmount',
        blockNumber: confirmedBlock,
      }),
      publicClient.readContract({
        address: config.coreAddress,
        abi: coreAbi,
        functionName: 'joiningFrozen',
      }),
      publicClient.readContract({
        address: config.paymentTokenAddress,
        abi: paymentTokenAbi,
        functionName: 'decimals',
      }),
      publicClient.readContract({
        address: config.coreAddress,
        abi: coreAbi,
        functionName: 'idsByWallet',
        args: [account],
        blockNumber: confirmedBlock,
      }),
      publicClient.readContract({
        address: config.coreAddress,
        abi: coreAbi,
        functionName: 'maxIdsPerWallet',
        blockNumber: confirmedBlock,
      }),
    ]);

    if (sponsorWallet === zeroAddress) {
      throw new JoinFlowError('invalid-sponsor', 'That sponsor is not present in finalized contract state.');
    }
    if (tokenDecimals !== 18) {
      throw new JoinFlowError('invalid-token', 'The configured payment token is not the required 18-decimal BTCB contract.', false);
    }
    if (freeze[0]) {
      const until = new Date(Number(freeze[1]) * 1_000).toLocaleString();
      throw new JoinFlowError('joining-frozen', `Joining is temporarily frozen for an era transition (scheduled until ${until}).`);
    }
    if (currentIds.length >= maxIdsPerWallet) {
      throw new JoinFlowError('wallet-limit', 'This wallet has reached the protocol limit for B-Dynamic IDs.', false);
    }

    const [tokenBalance, nativeBalance] = await Promise.all([
      publicClient.readContract({
        address: config.paymentTokenAddress,
        abi: paymentTokenAbi,
        functionName: 'balanceOf',
        args: [account],
      }),
      publicClient.getBalance({ address: account }),
    ]);

    return {
      sponsorId,
      sponsorWallet,
      era: eraFromChain(eraValue),
      entryAmount,
      tokenDecimals,
      tokenBalance,
      nativeBalance,
      confirmedBlock,
      currentIdCount: currentIds.length,
      existingIds: [...currentIds],
      maxIdsPerWallet,
    };
  } catch (cause) {
    throw normalizeJoinError(cause);
  }
}

function assertFunding(quote: JoinQuote): void {
  if (quote.tokenBalance < quote.entryAmount) {
    throw new JoinFlowError(
      'insufficient-btcb',
      `Insufficient BTCB. The wallet needs exactly ${formatJoinAmount(quote.entryAmount, quote.tokenDecimals)} BTCB plus BNB for gas.`,
    );
  }
  if (quote.nativeBalance === 0n) {
    throw new JoinFlowError('insufficient-gas', 'The wallet needs BNB to pay network gas.');
  }
}

async function waitForFinality(
  config: ProtocolConfiguration,
  hash: Hash,
  kind: 'approval' | 'join',
  onProgress: (progress: JoinProgress) => void,
): Promise<{ hash: Hash; receipt: TransactionReceipt }> {
  const publicClient = makePublicClient(config);
  let finalHash = hash;
  let replacementReason: 'cancelled' | 'repriced' | 'replaced' | undefined;
  onProgress({ stage: `${kind}-confirming`, hash });

  const receipt = await publicClient.waitForTransactionReceipt({
    hash,
    confirmations: config.confirmations,
    timeout: 300_000,
    onReplaced: (replacement) => {
      finalHash = replacement.transaction.hash;
      replacementReason = replacement.reason;
      onProgress({
        stage: `${kind}-replaced`,
        hash: finalHash,
        replacementReason,
      });
    },
  });

  if (replacementReason === 'cancelled') {
    throw new JoinFlowError('transaction-cancelled', 'The transaction was cancelled in the wallet. You can safely retry.');
  }
  if (receipt.status !== 'success') {
    throw new JoinFlowError('transaction-reverted', 'The mined transaction reverted. No protocol state was created.');
  }
  onProgress({ stage: `${kind}-finalized`, hash: finalHash });
  return { hash: finalHash, receipt };
}

function createdMemberId(receipt: TransactionReceipt, config: ProtocolConfiguration, account: Address, sponsorId: bigint): string {
  for (const log of receipt.logs) {
    if (getAddress(log.address) !== config.coreAddress) continue;
    try {
      const decoded = decodeEventLog({ abi: coreAbi, data: log.data, topics: log.topics });
      if (
        decoded.eventName === 'IdCreated' &&
        getAddress(decoded.args.wallet) === account &&
        decoded.args.sponsorId === sponsorId
      ) {
        return uintToMemberId(decoded.args.bDynamicId);
      }
    } catch {
      // Ignore logs from other events emitted by the core transaction.
    }
  }
  throw new JoinFlowError('missing-id-event', 'The transaction finalized without the expected IdCreated event. Verify it in BscScan.', false);
}

/** A join that was already broadcast in an earlier attempt; Retry continues from it instead of sending again. */
export interface JoinResume {
  joinHash?: Hash;
  fromBlock?: bigint;
}

const ID_CREATED_EVENT = parseAbiItem(
  'event IdCreated(uint256 indexed bDynamicId, address indexed wallet, uint256 indexed sponsorId, uint8 era, uint256 entryAmount, uint16 startingSlotCode, uint64 matrixVersion)',
);

/**
 * Wallets do not all return a canonical transaction hash (seen 2026-09-19 with TokenPocket: every
 * receipt poll answered "InvalidParams" because the hash was not 0x + 64 hex). Accept the common
 * variants, reject anything else so the flow falls back to finding the join by its event.
 */
export function normalizeTxHash(value: unknown): Hash | null {
  const text =
    typeof value === 'string'
      ? value
      : value && typeof value === 'object' && typeof (value as { hash?: unknown }).hash === 'string'
        ? (value as { hash: string }).hash
        : '';
  const clean = text.trim().replace(/^"+|"+$/g, '');
  const match = /^(?:0x)?([0-9a-fA-F]{64})$/.exec(clean);
  return match ? (`0x${match[1]!.toLowerCase()}` as Hash) : null;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Confirms a join without relying on the wallet's hash: waits for this wallet's IdCreated under this sponsor. */
async function waitForJoinEvent(
  config: ProtocolConfiguration,
  account: Address,
  sponsorId: bigint,
  fromBlock: bigint,
  onProgress: (progress: JoinProgress) => void,
): Promise<{ hash: Hash; receipt: TransactionReceipt }> {
  const publicClient = makePublicClient(config);
  onProgress({ stage: 'join-confirming', fromBlock });
  const deadline = Date.now() + 300_000;
  while (Date.now() < deadline) {
    try {
      const logs = await publicClient.getLogs({
        address: config.coreAddress,
        event: ID_CREATED_EVENT,
        args: { wallet: account, sponsorId },
        fromBlock,
        toBlock: 'latest',
      });
      const log = logs[logs.length - 1];
      const hash = log ? normalizeTxHash(log.transactionHash) : null;
      if (hash) return await waitForFinality(config, hash, 'join', onProgress);
    } catch (cause) {
      if (cause instanceof JoinFlowError) throw cause;
      // Transient RPC trouble: keep looking until the deadline.
    }
    await sleep(3_000);
  }
  throw new JoinFlowError(
    'rpc-unavailable',
    'The join has not appeared on-chain yet. Retry safely - it looks for this join first and never sends a second one.',
  );
}

/** Confirms by hash when the wallet gave a usable one, and by the IdCreated event otherwise or if the hash lookups fail. */
async function confirmJoin(
  config: ProtocolConfiguration,
  hash: Hash | null,
  account: Address,
  sponsorId: bigint,
  fromBlock: bigint,
  onProgress: (progress: JoinProgress) => void,
): Promise<{ hash: Hash; receipt: TransactionReceipt }> {
  if (hash) {
    try {
      return await waitForFinality(config, hash, 'join', onProgress);
    } catch (cause) {
      if (cause instanceof JoinFlowError) throw cause;
    }
  }
  return waitForJoinEvent(config, account, sponsorId, fromBlock, onProgress);
}

async function waitForAllowance(config: ProtocolConfiguration, account: Address, amount: bigint): Promise<void> {
  const publicClient = makePublicClient(config);
  const deadline = Date.now() + 300_000;
  while (Date.now() < deadline) {
    try {
      const allowance = await publicClient.readContract({
        address: config.paymentTokenAddress,
        abi: paymentTokenAbi,
        functionName: 'allowance',
        args: [account, config.coreAddress],
      });
      if (allowance === amount) return;
    } catch {
      // transient
    }
    await sleep(3_000);
  }
  throw new JoinFlowError('rpc-unavailable', 'The approval has not appeared on-chain yet. Retry safely.');
}

/**
 * Refuses to broadcast when this wallet already has a transaction in flight. Retrying while an
 * earlier join is still pending would mint a SECOND B-Dynamic ID for the same wallet.
 */
async function assertNoInFlightTransaction(publicClient: ReturnType<typeof makePublicClient>, account: Address): Promise<void> {
  const [pending, latest] = await Promise.all([
    publicClient.getTransactionCount({ address: account, blockTag: 'pending' }),
    publicClient.getTransactionCount({ address: account, blockTag: 'latest' }),
  ]);
  if (pending > latest) {
    throw new JoinFlowError(
      'transaction-in-flight',
      'A transaction from this wallet is still pending on the network. Wait for it to confirm, then Retry - sending another now could create a duplicate ID.',
    );
  }
}

export async function submitJoin(
  config: ProtocolConfiguration,
  account: Address,
  quote: JoinQuote,
  onProgress: (progress: JoinProgress) => void,
  resume?: JoinResume,
): Promise<JoinResult> {
  if (!window.ethereum) throw new JoinFlowError('wallet-missing', 'No injected EVM wallet is available.');
  assertFunding(quote);

  const chain = config.chainId === 56 ? bsc : bscTestnet;
  const publicClient = makePublicClient(config);
  const walletClient = createWalletClient({
    account,
    chain,
    transport: custom(window.ethereum as unknown as EIP1193Provider),
  });

  try {
    // Resume: the join was already sent in a previous attempt that lost contact with the RPC.
    // Wait on THAT hash. Never re-approve, never re-join - either would create a second ID.
    if (resume && (resume.joinHash || resume.fromBlock !== undefined)) {
      const resumeHash = resume.joinHash ? normalizeTxHash(resume.joinHash) : null;
      const fromBlock = resume.fromBlock ?? (await publicClient.getBlockNumber()) - 4_000n;
      const finalizedJoin = await confirmJoin(config, resumeHash, account, quote.sponsorId, fromBlock > 0n ? fromBlock : 0n, onProgress);
      return {
        memberId: createdMemberId(finalizedJoin.receipt, config, account, quote.sponsorId),
        joinHash: finalizedJoin.hash,
        blockNumber: finalizedJoin.receipt.blockNumber,
      };
    }
    await assertNoInFlightTransaction(publicClient, account);

    let approvalHash: Hash | undefined;
    const allowance = await publicClient.readContract({
      address: config.paymentTokenAddress,
      abi: paymentTokenAbi,
      functionName: 'allowance',
      args: [account, config.coreAddress],
    });

    if (needsExactApproval(allowance, quote.entryAmount)) {
      const approvalSimulation = await publicClient.simulateContract({
        account,
        address: config.paymentTokenAddress,
        abi: paymentTokenAbi,
        functionName: 'approve',
        args: [config.coreAddress, quote.entryAmount],
      });
      const submittedApprovalHash = normalizeTxHash(await walletClient.writeContract(approvalSimulation.request));
      if (submittedApprovalHash) {
        onProgress({ stage: 'approval-submitted', hash: submittedApprovalHash });
        try {
          const finalizedApproval = await waitForFinality(config, submittedApprovalHash, 'approval', onProgress);
          approvalHash = finalizedApproval.hash;
        } catch (cause) {
          if (cause instanceof JoinFlowError) throw cause;
          await waitForAllowance(config, account, quote.entryAmount);
        }
      } else {
        onProgress({ stage: 'approval-confirming' });
        await waitForAllowance(config, account, quote.entryAmount);
      }
    } else {
      onProgress({ stage: 'approval-skipped' });
    }

    const freshQuote = await loadJoinQuote(config, account, uintToMemberId(quote.sponsorId));
    if (freshQuote.era !== quote.era || freshQuote.entryAmount !== quote.entryAmount) {
      throw new JoinFlowError('quote-changed', 'The live era or entry amount changed. Review a fresh quote before trying again.');
    }
    assertFunding(freshQuote);

    const latestBlock = await publicClient.getBlock();
    const deadline = latestBlock.timestamp + BigInt(config.deadlineSeconds);
    const joinSimulation = await publicClient.simulateContract({
      account,
      address: config.coreAddress,
      abi: coreAbi,
      functionName: 'join',
      args: [freshQuote.sponsorId, freshQuote.era, freshQuote.entryAmount, deadline],
    });
    const submittedJoinHash = normalizeTxHash(await walletClient.writeContract(joinSimulation.request));
    onProgress({ stage: 'join-submitted', ...(submittedJoinHash ? { hash: submittedJoinHash } : {}), fromBlock: latestBlock.number });
    const finalizedJoin = await confirmJoin(config, submittedJoinHash, account, freshQuote.sponsorId, latestBlock.number, onProgress);

    return {
      memberId: createdMemberId(finalizedJoin.receipt, config, account, freshQuote.sponsorId),
      ...(approvalHash ? { approvalHash } : {}),
      joinHash: finalizedJoin.hash,
      blockNumber: finalizedJoin.receipt.blockNumber,
    };
  } catch (cause) {
    throw normalizeJoinError(cause);
  }
}

